import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * One row per client-recorded softphone observability event.
 *
 * Mirrors the wire shape from
 * `libs/core/src/softphone/observability/SoftphoneObservabilityEvent.ts`,
 * persisted by `SoftphoneObservabilityService.ingest()` after the
 * batched POST hits `/api/softphone/observability/events`.
 *
 * Indexed for the three query patterns the dashboards need:
 *   - per-session call timeline (sessionId + time)
 *   - per-call drilldown (callId)
 *   - per-event-type aggregations (eventType + time)
 *
 * Per docs/claude/softphone/09-observability.md the v1 retention window is
 * 90 days hot. The lifecycle policy lands alongside the dashboards
 * that consume the table — this entity exists now so writes have a
 * destination from the moment Phase 1 closes.
 */
@Index('softphone_observability_event_session_idx', [
	'sessionId',
	'eventTimestamp',
])
@Index('softphone_observability_event_type_idx', [
	'eventType',
	'eventTimestamp',
])
@Index('softphone_observability_event_call_idx', ['callId', 'eventTimestamp'])
@Entity('softphone_observability_event')
export class SoftphoneObservabilityEventEntity extends BaseEntity {
	/** Client-generated UUID — dedupes retries. */
	@Column('varchar', { name: 'uuid', length: 64 })
	@Index('softphone_observability_event_uuid_idx', { unique: true })
	public uuid!: string;

	/** Wall-clock timestamp at the moment the event was recorded
	 * (ms since epoch from the client). */
	@Column('bigint', { name: 'eventTimestamp', unsigned: true })
	public eventTimestamp!: string;

	@Column('varchar', { name: 'eventType', length: 64 })
	public eventType!: string;

	@Column('varchar', { name: 'sessionId', length: 128 })
	public sessionId!: string;

	@Column('varchar', { name: 'callId', length: 64, nullable: true })
	public callId!: string | null;

	@Column('varchar', { name: 'role', length: 16, nullable: true })
	public role!: string | null;

	@Column('json', { name: 'payload', nullable: true })
	public payload!: Record<string, unknown> | null;
}
